import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { LicensesActivityService, ListHelperService, ProfileService, StoreService } from 'core/services';
import { DateTime } from 'luxon';

@Injectable()
export class LicensesActivityListService {
  private userRole: any;
  private userLang: string;

  private stateLicensesOptions = [
    { key: 'any', value: this.translate.instant('properties.any') },
    { key: 'created', value: this.translate.instant('licenses.state.created') },
    { key: 'completed', value: this.translate.instant('licenses.state.completed') },
    { key: 'cancelled', value: this.translate.instant('licenses.state.cancelled') },
    { key: 'beforeDemo', value: this.translate.instant('licenses.state.beforeDemo') },
    { key: 'afterDemo', value: this.translate.instant('licenses.state.afterDemo') },
    // { key: 'notAvailable', value: this.translate.instant('licenses.state.notAvailable') },
    { key: 'playing', value: this.translate.instant('licenses.state.playing') }
  ];
  private stateSuperlicensesOptions = [
    { key: 'any', value: this.translate.instant('properties.any') },
    { key: 'created', value: this.translate.instant('licenses.state.created') },
    { key: 'completed', value: this.translate.instant('licenses.state.completed') },
    { key: 'cancelled', value: this.translate.instant('licenses.state.cancelled') },
    { key: 'playing', value: this.translate.instant('licenses.state.playing') }
  ];

  constructor(
    private licensesActivityService: LicensesActivityService,
    private translate: TranslateService,
    private profileService: ProfileService,
    private listHelper: ListHelperService,
    private router: Router
  ) {
    this.profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
    this.userLang = this.profileService.getLang();
  }

  async getDatasource(listId, options: any = {}, group) {
    const dataSource = new TableDatasource({
      sort: '-values.points',
      columns: this.getColumns(group),
      load: (props) => this.fetch(props, dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { groupId, date, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());

    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      groupId,
      date,
      $filter
    };
    return this.licensesActivityService.fetch(paginationOptions).then((response) => response);
  }

  getColumns(group, sortable = true) {
    const isSupergroup = group.type === 'itinerary';
    const columns = [
      {
        source: 'user.name',
        style: { width: '80px' },
        label: this.translate.instant('students.header.name'),
        allowFiltering: false,
        sortable: sortable
      },
      {
        source: 'user.surname',
        style: { width: '80px' },
        label: this.translate.instant('nouns.surname'),
        allowFiltering: false,
        sortable: sortable
      },
      {
        source: 'user.email',
        style: { width: '140px' },
        label: this.translate.instant('nouns.email'),
        allowFiltering: false,
        sortable: sortable
      },
      // Filter old lang
      // {
      //   source: 'user.lang',
      //   style: { width: '140px' },
      //   label: this.translate.instant('nouns.lang'),
      //   sortable: false,
      //   searchRenderer: (renderer, value, callback) => {
      //     const localesOptions = this.locales.getDefaultOptions();
      //     localesOptions.unshift({ key: 'any', value: this.translate.instant('properties.any') });
      //     return renderer`
      //     <gl-select
      //     small
      //     .value="${value || 'any'}"
      //     .options="${localesOptions}"
      //     @onChange="${(ev) => callback(ev.detail.value !== 'any' ? ev.detail.value : null)}">
      //     </gl-select>
      //     `;
      //   }
      // },
      {
        source: 'values.points',
        style: { width: '140px' },
        label: this.translate.instant('reports.points'),
        allowFiltering: false,
        sortable: sortable,
        valueParser: (value) => {
          return value == null ? '' : '' + value;
        }
      },
      isSupergroup
        ? {
            source: 'currentItineraryModule',
            style: { width: '80px' },
            label: this.translate.instant('reports.course'),
            allowFiltering: false,
            sortable: sortable,
            valueParser: (value) => {
              return value?.courseTitle
                ? value?.courseTitle[this.userLang] || value?.courseTitle['en']
                : value?.gameName || '';
            }
          }
        : null,
      {
        source: 'values.turn',
        style: { width: '80px' },
        label: this.translate.instant('reports.level'),
        allowFiltering: false,
        sortable: sortable,
        valueParser: (value) => {
          return value == null ? '' : '' + (value + 1);
        }
      },
      {
        source: 'values.startDate',
        style: { width: '140px' },
        label: this.translate.instant('reports.firstConnection'),
        sortable: sortable,
        allowFiltering: false,
        dataType: DataTypes.datetime,
        valueParser: (value, license) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, license.timezone, format);
        }
      },
      {
        source: 'values.endDate',
        style: { width: '140px' },
        label: this.translate.instant('reports.lastConnection'),
        sortable: true,
        allowFiltering: false,
        valueParser: (value, license) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, license.timezone, format);
        }
      },
      {
        source: 'values.durationSeconds',
        style: { width: '80px' },
        label: this.translate.instant('reports.duration'),
        sortable: true,
        allowFiltering: false,
        valueParser: (value) => this.listHelper.parsePlaytime(value)
      },
      {
        source: 'status',
        style: { width: '120px' },
        label: this.translate.instant('nouns.status'),
        sortable: false,
        searchRenderer: (renderer, value, callback) => {
          const licensesStatus = isSupergroup ? this.stateSuperlicensesOptions : this.stateLicensesOptions;
          return renderer`
            <gl-select
            small
            .value="${value || 'any'}"
            .options="${licensesStatus}"
            @onChange="${(ev) => callback(ev.detail.value !== 'any' ? ev.detail.value : null)}">
            </gl-select>
            `;
        },
        valueParser: (value) => {
          value =
            value && isSupergroup && ['beforeDemo', 'afterDemo'].includes(value)
              ? 'playing'
              : value
              ? value
              : 'notAvailable';
          return this.translate.instant('licenses.state.' + value);
        }
      },
      {
        source: 'values.gameProgress',
        style: { width: '80px' },
        label: this.translate.instant('reports.gameProgress'),
        allowFiltering: false,
        sortable: sortable,
        valueParser: (value, license) => {
          const progress = license.values?.gameProgress ? Math.round(license.values.gameProgress) : 0;
          return progress + '%';
        }
      },
      group && group.hasBonus
        ? {
            source: 'bonus.isReached',
            style: { width: '80px' },
            label: this.translate.instant('reports.bonus'),
            dataType: DataTypes.boolean,
            allowFiltering: false,
            sortable: false
          }
        : null,
      {
        alignment: 'left',
        allowFiltering: false,
        sortable: false,
        visible: this.profileService.canExecute('licenses.report'),
        showInColumnChooser: false,
        style: { width: '50px' },
        cellRenderer: (renderer, value, license) => {
          const url = `/licenses/${license.id}/report`;
          return renderer`
            <gl-button icon icon-center="glicon-chart-pie"
        @click="${(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          if (ev.shiftKey || ev.ctrlKey || ev.metaKey) {
            // eslint-disable-next-line
            window.open(url, '_blank');
          } else {
            this.router.navigateByUrl(url);
          }
        }}"
        title="${this.translate.instant('actions.showReport')}"
  
      >
      </gl-button>
  
              `;
        }
      }
    ];
    return columns.filter((col) => col !== null);
  }

  getCustomFilters() {
    return {};
  }
}
